<template lang="pug">
  div
    div.Music
      p.center-loader(v-if="isBusy")
        b-spinner.align-middle.mx-2
        strong Loading...
      div.content-to-fade.enforce-max-width(:class="ready")
        b-row.mt-5.pb-5.album-row(v-for="(album, i) in albumList", :key="album.id || i", no-gutters)
          b-col.fix-gutters.album-info(cols="12", md="4", v-if="album.platform==='Apple Music'")
            h6.d-md-none#mobile-track-title
              span {{ album.name }}
              br
              span.artist {{ album.artist }}
            b-img.mb-3(:src="album.artworkurl", fluid, center, :alt="album.name")
            p.my-1.subtitle
              span(v-for="genre in album.genres", :key="genre")
                b-badge.mx-1(variant="light") {{ genre }}
            p.my-1.small
              span {{ album.trackcount }} tracks
              |&nbsp;-&nbsp;
              span Released {{ album.releasedate | dateParse('YYYY-MM-DD') | dateFormat('dddd, MMM D, YYYY')}}
            p.mt-1.mb-3.small.text-faded {{ album.copyright }}
            b-button.d-block.mt-3(v-if="album.appleurl", :href="album.appleurl", target="_blank", size="sm", variant="info") Buy on Apple/iTunes
            b-button.d-block.mt-3(v-if="album.bandcampurl", :href="album.bandcampurl", target="_blank", size="sm", variant="info") Buy on Bandcamp
            b-button.d-block.mt-3(v-if="album.spotifyurl", :href="album.spotifyurl", target="_blank", size="sm", variant="info") Listen on Spotify
          b-col.text-left.album-tracks(cols="12", md="8", v-if="album.platform==='Apple Music'")
            h2.pl-3.d-none.d-md-block {{ album.name }}
            h5.pl-3.text-faded.d-none.d-md-block {{ album.artist }}
            ul.mt-3.tracklist
              li.pointer(v-for="track in album.tracks", :key="track.id", @click="play(track.id)", :class="{ playing: currentTrack===track.id }")
                span.pr-1.text-faded.text-center.pl-1 {{ track.tracknumber }}.
                span.pr-1.text-secondary-custom.headings
                  |{{ track.name }}
                  b-icon-soundwave.pl-2.playing(v-if="currentTrack===track.id")
                span.composer.text-faded.small {{ track.composer }}
                span.pr-1.text-right
                  span.duration {{ track.duration }}
                  audio(:src="track.preview", :id="track.id", @ended="endPlay()")
                  b-button.ml-3.preview-button(size="sm", variant="info")
                    span
                      b-icon-pause-fill.pr-1(v-if="currentTrack===track.id")
                      b-icon-play-fill.pr-1(v-else)
                    span Preview
          b-col.fix-gutters.album-info(cols="12", md="4", v-if="album.platform==='Bandcamp'")
            b-img.mb-3(:src="album.artworkurl", fluid, center, :alt="album.id")
            b-button.d-block.mt-3(v-if="album.bandcampurl", :href="album.bandcampurl", target="_blank", size="sm", variant="info") Buy on Bandcamp
          b-col.text-left.album-tracks(cols="12", md="8", v-if="album.platform==='Bandcamp'")
            iframe(
              :src="`https://bandcamp.com/EmbeddedPlayer/album=${album.id}/size=large/bgcol=ffffff/linkcol=63b2cc/artwork=none/transparent=true/tracklist=true/`"
              style="border:0; width:100%; height:472px; outline-style: auto; outline-color: white;"
              seamless
            )
</template>

<script>
export default {
  name: "Music",
  data() {
    return {
      fields: ["tracknumber", "name", "duration"],
      isBusy: true,
      ready: "notready",
      error: false,
      albumList: [], // both Apple Music & Bandcamp
      appleMusicRaw: [],
      audio: new Audio(),
      currentTrack: ""
    };
  },
  methods: {
    endPlay() {
      this.currentTrack = "";
    },
    play(handle) {
      if (this.currentTrack === handle) {
        if (!this.audio.paused) {
          this.audio.pause();
          this.currentTrack = "";
        } else {
          this.audio.play();
        }
      } else {
        if (!this.audio.paused) this.audio.pause();
        this.audio = document.getElementById(handle);
        this.audio.play();
        this.currentTrack = handle;
      }
    }
  },
  created() {
    (async () => {
      try {
        const sheetResp = await fetch(process.env.VUE_APP_ALBUMSHEET, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          }
        });
        const sheetData = await sheetResp.json();

        const albumSheetList = [];
        const appleIds = [];
        sheetData.values.forEach(row => {
          const entry = {
            platform: row[0],
            id: row[1],
            appleurl: row[3],
            spotifyurl: row[4],
            bandcampurl: row[5],
            artworkurl: row[6]
          };
          if (entry.platform === "Apple Music") appleIds.push(entry.id);
          albumSheetList.push(entry);
        });

        let appleData = [];
        if (appleIds.length) {
          const resp = await this.$axios.get(
            "/.netlify/functions/apple-music",
            {
              params: { ids: appleIds.join(",") }
            }
          );
          appleData = resp.data.data;
          this.appleMusicRaw = appleData;
        }

        const appleMap = {};
        appleData.forEach(item => (appleMap[item.id] = item));

        this.albumList = albumSheetList
          .map(entry => {
            if (entry.platform === "Apple Music") {
              const val = appleMap[entry.id];
              if (!val) return null;
              return {
                platform: "Apple Music",
                id: val.id,
                name: val.attributes.name,
                artist: val.attributes.artistName,
                artworkbg: val.attributes.artwork.bgColor,
                artworkurl: val.attributes.artwork.url
                  .replace("{w}", val.attributes.artwork.width)
                  .replace("{h}", val.attributes.artwork.height),
                copyright: val.attributes.copyright,
                genres: val.attributes.genreNames,
                releasedate: val.attributes.releaseDate,
                trackcount: val.attributes.trackCount,
                url: val.attributes.url,
                appleurl: entry.appleurl,
                spotifyurl: entry.spotifyurl,
                bandcampurl: entry.bandcampurl,
                tracks: val.relationships.tracks.data.map(trackData => {
                  const secs = Math.round(
                    trackData.attributes.durationInMillis / 1000
                  );
                  const m = new Date(secs * 1000).getUTCMinutes();
                  const s = ("0" + new Date(secs * 1000).getUTCSeconds()).slice(
                    -2
                  );
                  return {
                    id: trackData.id,
                    tracknumber: trackData.attributes.trackNumber,
                    name: trackData.attributes.name,
                    composer: trackData.attributes.composerName,
                    genres: trackData.attributes.genreNames,
                    preview: trackData.attributes.previews[0]?.url || "",
                    url: trackData.attributes.url,
                    duration: `${m}:${s}`
                  };
                })
              };
            }
            if (entry.platform === "Bandcamp") {
              return {
                platform: "Bandcamp",
                id: entry.id,
                bandcampurl: entry.bandcampurl,
                artworkurl: entry.artworkurl
              };
            }
            return null;
          })
          .filter(a => a);
      } catch (err) {
        console.error(err);
        this.error = true;
      } finally {
        this.ready = "ready";
        this.isBusy = false;
      }
    })();
  }
};
</script>

<style scoped lang="less">
.content-to-fade {
  opacity: 0;
  -webkit-transition: opacity 2.8s;
  -moz-transition: opacity 2.8s;
  transition: opacity 2.8s;
  &.ready {
    opacity: 1;
  }
}
.badge {
  font-variant: normal;
  font-weight: 400;
}
.album-row {
  justify-content: center;
}
.album-tracks {
  max-width: 700px;
}
ul.tracklist {
  display: table;
  padding-left: 0;
  width: 100%;
  > li {
    display: flex !important;
    flex-wrap: nowrap !important;
    flex-flow: row;
    list-style: none;
    justify-content: center;
    align-items: center;
    > span {
      transition: 0.5s all ease;
      color: var(--primary-color);
      position: relative;
      flex: auto;
      padding: 0.55rem 0;
      border-top: 6px solid var(--bg-color);
      border-bottom: 6px solid var(--bg-color);
      background: var(--card-color);
      line-height: 36px;
      .playing {
        font-size: 32px;
        color: var(--secondary-color);
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
      }
      &:nth-child(1) {
        flex: initial;
        min-width: 8%;
      }
      &:nth-child(2) {
        flex: auto;
      }
      &:nth-child(3) {
        // flex: initial;
        display: none;
      }
      button {
        white-space: nowrap !important;
        margin-right: 6px;
      }
    }
    &:hover > span {
      background: var(--bg-color);
      color: var(--secondary-color) !important;
    }
  }
}
</style>
